import { InformationCircleIcon } from "@heroicons/react/20/solid";
import {
    BusinessProfileProgress,
    BusinessVerificationStatus,
    capitalize,
} from "@sp-crm/core";
import { SupportEmailButton, SupportEmailLink } from "components/app/support-email-link";
import { Badge, BadgeProps } from "components/ui/badge";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useState } from "react";
import {
    businessDisplayName,
    officesPhrase,
    toBusinessProfileDetails,
} from "./business-profile-types";
import {
    businessNeedsAttention,
    callingStatusOf,
    customerBusinessStyles,
} from "./calling-status";
import {
    BusinessStatusPanels,
    StatusIcon,
    textingAwaitingApproval,
} from "./status-panels";
import { BusinessProfileOutput } from "./use-business-verification";

const cardClasses = "rounded-lg border border-gray-200 p-4 space-y-4";

export interface ProgressBadgeStyle {
    label: string;
    type: BadgeProps["type"];
}

export const progressBadges: Record<BusinessProfileProgress, ProgressBadgeStyle> = {
    inProgress: { label: "Draft", type: "info" },
    readyForReview: { label: "Ready for review", type: "success" },
    submitted: { label: "Submitted", type: "highlight" },
};

const bodyClasses = "text-sm text-gray-700 mt-2 max-w-2xl";

const submittedText = "Sent to carriers. We'll email you once they've reviewed it.";

type SubmittedStatus = Exclude<BusinessVerificationStatus, "draft">;

const submittedStatusOf = (status: BusinessVerificationStatus): SubmittedStatus | null =>
    status === "draft" ? null : status;

export const BusinessStatusBadge: React.FC<{ profile: BusinessProfileOutput }> = ({
    profile,
}) => {
    const style = customerBusinessStyles[profile.verificationStatus];
    return <Badge value={style.label} type={style.badgeType} />;
};

const VerificationText: React.FC<{ status: SubmittedStatus }> = ({ status }) => {
    switch (status) {
        case "inReview":
            return <p className={bodyClasses}>{submittedText}</p>;
        case "needsAttention":
            return (
                <p className={bodyClasses}>
                    Carriers couldn&apos;t verify this business yet. We&apos;ll contact
                    you if we need anything.
                </p>
            );
        case "verified":
            return null;
    }
    const exhaustiveCheck: never = status;
    throw new Error(`Unhandled business verification status: ${exhaustiveCheck}`);
};

interface BusinessCardProps {
    profile: BusinessProfileOutput;
    isMultiOffice: boolean;
    coveredOffices: (officeIds: BusinessProfileOutput["officeIds"]) => string;
}

const draftText = "Our team is preparing this business for carriers.";

const CardBody: React.FC<{ status: BusinessVerificationStatus }> = ({ status }) => {
    const submittedStatus = submittedStatusOf(status);
    return submittedStatus ? (
        <VerificationText status={submittedStatus} />
    ) : (
        <p className={bodyClasses}>{draftText}</p>
    );
};

const NeedsAttention: React.FC<{ what: string }> = ({ what }) => (
    <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-700">
        <StatusIcon tone="bad" sizeClass="size-4" />
        {what} needs attention
    </p>
);

export const ContactSupportToChange: React.FC = () => (
    <InlineBanner type="info" showIcon={false}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="flex items-center gap-3">
                <InformationCircleIcon
                    aria-hidden
                    className="size-5 shrink-0 text-blue-400"
                />
                Our team manages your verification. Need to change something?
            </span>
            <SupportEmailButton
                label="Contact Support"
                subject="Change SMS & Voice Business Verification"
            />
        </div>
    </InlineBanner>
);

const unverifiedText = "your calls may show up as spam and some texts may not arrive.";

const ContactSupportToStart: React.FC = () => (
    <>
        Our team handles verification for you.{" "}
        <SupportEmailLink
            label="Contact Support"
            subject="Start SMS & Voice Business Verification"
        />{" "}
        to get started.
    </>
);

const textingAwaitsCarriers = (
    profile: Pick<BusinessProfileOutput, "verificationStatus" | "textingStatus">,
): boolean =>
    profile.verificationStatus === "verified" && profile.textingStatus === "inReview";

export const BusinessCard: React.FC<BusinessCardProps> = props => {
    const { profile, isMultiOffice, coveredOffices } = props;
    const details = toBusinessProfileDetails(profile);
    const callingNeedsAttention =
        callingStatusOf(profile.trustProducts) === "needsAttention";
    const [expanded, setExpanded] = useState(businessNeedsAttention(profile));

    return (
        <div className={cardClasses}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">
                            {businessDisplayName(details)}
                        </span>
                        <BusinessStatusBadge profile={profile} />
                    </div>
                    {isMultiOffice ? (
                        <p className="text-sm text-gray-500 mt-0.5">
                            Covers {coveredOffices(profile.officeIds)}
                        </p>
                    ) : null}
                    <CardBody status={profile.verificationStatus} />
                    {profile.textingStatus === "needsAttention" ? (
                        <NeedsAttention what="Texting" />
                    ) : null}
                    {textingAwaitsCarriers(profile) ? (
                        <div className="mt-2">
                            <InlineBanner type="info">
                                Texting in review. {textingAwaitingApproval}.
                            </InlineBanner>
                        </div>
                    ) : null}
                    {callingNeedsAttention ? <NeedsAttention what="Calling" /> : null}
                </div>
                <div className="shrink-0">
                    <SecondaryButton
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => setExpanded(!expanded)}>
                        {expanded ? "Hide details" : "View details"}
                    </SecondaryButton>
                </div>
            </div>
            {expanded ? <BusinessStatusPanels profile={profile} /> : null}
        </div>
    );
};

export const NotStartedCard: React.FC = () => (
    <div className={cardClasses}>
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">Your business</span>
            <Badge value="Not started" type="info" />
        </div>
        <p className="text-sm text-gray-700 max-w-2xl">
            Until your business is verified, {unverifiedText} <ContactSupportToStart />
        </p>
    </div>
);

export const UncoveredOfficesCard: React.FC<{ officeNames: string[] }> = ({
    officeNames,
}) => (
    <div className="rounded-lg border border-dashed border-gray-300 p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">
                {capitalize(officesPhrase(officeNames, 0))}
            </span>
            <Badge value="Not started" type="info" />
        </div>
        <p className="text-sm text-gray-700 max-w-2xl">
            Until {officeNames.length === 1 ? "it's" : "they're"} verified,{" "}
            {unverifiedText} <ContactSupportToStart />
        </p>
    </div>
);
